import {
  AfterViewChecked,
  Component,
  DestroyRef,
  ElementRef,
  OnInit,
  ViewChild,
  inject,
  signal,
  computed
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatRippleModule } from '@angular/material/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationService } from '../../../../core/navigation/navigation.service';
import {
  NavigationDropdown,
  NavigationItem,
  NavigationLink,
  NavigationSubheading
} from '../../../../core/navigation/navigation-item.interface';

export interface MenuSearchHit {
  label: string;
  route: string;
  icon?: string;
  path: string;
}

@Component({
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatIconModule,
    MatButtonModule,
    MatRippleModule
  ],
  selector: 'vex-search-modal',
  templateUrl: './search-modal.component.html',
  styleUrls: ['./search-modal.component.scss']
})
export class SearchModalComponent implements OnInit, AfterViewChecked {
  @ViewChild('searchInput') searchInput?: ElementRef<HTMLInputElement>;
  @ViewChild('listBody') listBody?: ElementRef<HTMLElement>;

  private readonly destroyRef = inject(DestroyRef);
  private readonly navigationService = inject(NavigationService);
  private readonly router = inject(Router);
  private readonly dialogRef =
    inject<MatDialogRef<SearchModalComponent>>(MatDialogRef);

  readonly query = new FormControl('', { nonNullable: true });
  readonly allHits = signal<MenuSearchHit[]>([]);
  readonly activeIndex = signal(0);

  readonly filteredHits = computed(() => {
    const q = this.normalize(this.queryValue());
    const items = this.allHits();
    if (!q) {
      return items;
    }
    return items.filter(
      (item) =>
        this.normalize(item.label).includes(q) ||
        this.normalize(item.path).includes(q) ||
        this.normalize(item.route).includes(q)
    );
  });

  readonly isMac =
    typeof navigator !== 'undefined' &&
    /Mac|iPhone|iPad|iPod/i.test(navigator.platform || navigator.userAgent);

  readonly shortcutHint = this.isMac ? '⌘ Space' : 'Ctrl Space';

  /** Reactive query mirror so computed() updates on typing. */
  private readonly queryValue = signal('');
  private pendingScrollIntoView = false;

  ngOnInit(): void {
    this.navigationService.items$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((items) => {
        this.allHits.set(this.flattenMenus(items));
        this.activeIndex.set(0);
      });

    this.query.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        this.queryValue.set(value ?? '');
        this.activeIndex.set(0);
        this.pendingScrollIntoView = true;
      });
  }

  ngAfterViewChecked(): void {
    if (!this.pendingScrollIntoView) {
      return;
    }
    this.pendingScrollIntoView = false;
    this.scrollActiveIntoView();
  }

  onKeydown(event: KeyboardEvent): void {
    const hits = this.filteredHits();
    if (!hits.length) {
      if (event.key === 'Escape') {
        this.close();
      }
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.activeIndex.update((i) => (i + 1) % hits.length);
        this.pendingScrollIntoView = true;
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.activeIndex.update((i) => (i - 1 + hits.length) % hits.length);
        this.pendingScrollIntoView = true;
        break;
      case 'Enter':
        event.preventDefault();
        this.select(hits[this.activeIndex()] ?? hits[0]);
        break;
      case 'Escape':
        event.preventDefault();
        this.close();
        break;
      default:
        break;
    }
  }

  private scrollActiveIntoView(): void {
    const container = this.listBody?.nativeElement;
    if (!container) {
      return;
    }
    const active = container.querySelector(
      '.menu-spotlight__item--active'
    ) as HTMLElement | null;
    active?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }

  select(hit: MenuSearchHit | undefined): void {
    if (!hit?.route) {
      return;
    }
    void this.router.navigateByUrl(hit.route);
    this.close();
  }

  close(): void {
    this.dialogRef.close();
  }

  trackByRoute(_index: number, hit: MenuSearchHit): string {
    return hit.route + hit.label;
  }

  private flattenMenus(
    items: NavigationItem[],
    parents: string[] = []
  ): MenuSearchHit[] {
    const hits: MenuSearchHit[] = [];

    for (const item of items) {
      if (this.isLink(item)) {
        const route = this.routeToString(item.route);
        if (!route || route === '#') {
          continue;
        }
        hits.push({
          label: item.label,
          route,
          icon: item.icon,
          path: [...parents, item.label].join(' › ')
        });
        continue;
      }

      if (this.isDropdown(item) || this.isSubheading(item)) {
        const nextParents =
          item.type === 'subheading'
            ? [...parents, item.label]
            : [...parents, item.label];
        hits.push(...this.flattenMenus(item.children, nextParents));
      }
    }

    return hits;
  }

  private routeToString(route: string | any): string {
    if (typeof route === 'string') {
      return route;
    }
    if (Array.isArray(route)) {
      return (
        '/' +
        route
          .filter((part) => part != null && part !== '')
          .map(String)
          .join('/')
      ).replace(/\/+/g, '/');
    }
    return '';
  }

  private normalize(value: string): string {
    return value
      .toLowerCase()
      .normalize('NFD')
      .replace(/\p{M}/gu, '')
      .trim();
  }

  private isLink(item: NavigationItem): item is NavigationLink {
    return item.type === 'link';
  }

  private isDropdown(item: NavigationItem): item is NavigationDropdown {
    return item.type === 'dropdown';
  }

  private isSubheading(item: NavigationItem): item is NavigationSubheading {
    return item.type === 'subheading';
  }
}
